import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type Phaser from 'phaser';
import type { FinishPlayAttemptInput } from '../../../shared/results';
import {
  EventBus,
  RACE_GAME_FINISHED_EVENT,
  RACE_HUD_UPDATE_EVENT,
  type RaceGameResult,
  type RaceHudState,
  type RaceHudUpdate,
} from '../../game/EventBus';
import { FullscreenGameShell } from '../../game/FullscreenGameShell';
import type { TurboDuelConfig } from './domain/raceLevels';
import { startTurboDuelGame } from './game/startTurboDuelGame';

type TurboDuelGameProps = {
  config: TurboDuelConfig;
  label?: string;
  restartEnabled?: boolean;
  onFinished?: (result: FinishPlayAttemptInput) => void;
};

export function TurboDuelGame({ config, label = 'Turbo Düellosu oyun tuvali', restartEnabled = true, onFinished }: TurboDuelGameProps) {
  const gameRef = useRef<Phaser.Game | null>(null);
  const onFinishedRef = useRef(onFinished);
  const gameId = `turbo-duel-${useId().replace(/:/g, '')}`;
  const containerId = `${gameId}-container`;
  const [hud, setHud] = useState<RaceHudState>({
    question: 1, totalQuestions: config.questions.length, phase: 'vehicle-select', score: 0, place: 1, streak: 0,
    remainingMs: config.questions[0]?.timeLimitSeconds == null ? null : config.questions[0].timeLimitSeconds * 1000,
  });
  useEffect(() => { onFinishedRef.current = onFinished; }, [onFinished]);
  useLayoutEffect(() => {
    const handleHud = ({ gameId: source, hud: next }: RaceHudUpdate) => { if (source === gameId) setHud(next); };
    const handleFinished = ({ gameId: source, result }: RaceGameResult) => { if (source === gameId) onFinishedRef.current?.(result); };
    EventBus.on(RACE_HUD_UPDATE_EVENT, handleHud);
    EventBus.on(RACE_GAME_FINISHED_EVENT, handleFinished);
    gameRef.current = startTurboDuelGame(containerId, gameId, config, restartEnabled);
    return () => {
      EventBus.off(RACE_HUD_UPDATE_EVENT, handleHud);
      EventBus.off(RACE_GAME_FINISHED_EVENT, handleFinished);
      gameRef.current?.destroy(true); gameRef.current = null;
    };
  }, [config, containerId, gameId, restartEnabled]);
  return (
    <FullscreenGameShell
      gameRef={gameRef}
      className="turbo-duel-shell"
      toolbar={<>
        <strong className="toolbar-level">SORU {hud.question} / {hud.totalQuestions}</strong>
        <span className="race-place">{hud.place}. SIRA</span>
        {hud.remainingMs != null && <strong className="bridge-timer">{Math.ceil(hud.remainingMs / 1000)} sn</strong>}
        <span className="toolbar-score">Puan: {hud.score}</span>
        <span className="race-streak">Seri: {hud.streak}</span>
      </>}
    >
      <div className="game-container" id={containerId} role="application" aria-label={label} />
    </FullscreenGameShell>
  );
}
